import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { integrationApi } from '@/api/endpoints'
import { Button, EnterChip } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { toast } from '@/store/toast'

type Screen = 'intro' | 'choose' | 'signin' | 'create' | 'creating' | 'otp'
type SignInMethod = 'password' | 'otp'

interface Props {
  onDone: () => void
  onSkip: () => void
  /** Reports leaving the intro screen, so the host can swap the headline. */
  onExpand?: (expanded: boolean) => void
}

/**
 * Optional Naukri linking, shown inside step 1.
 *
 * Kept as a self-contained state machine so it can later be lifted into
 * Settings without change — the user can reach the same flow from either place.
 */
export function NaukriConnect({ onDone, onSkip, onExpand }: Props) {
  const queryClient = useQueryClient()
  const [screen, setScreen] = useState<Screen>('intro')

  const goTo = (next: Screen) => {
    setScreen(next)
    onExpand?.(next !== 'intro')
  }
  const [method, setMethod] = useState<SignInMethod>('password')

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [mobile, setMobile] = useState('')
  const [fullName, setFullName] = useState('')
  const [code, setCode] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['integrations'] })
    queryClient.invalidateQueries({ queryKey: ['top-matches'] })
  }

  // Linking is done once sign-in succeeds. The first pull (fetching every tab,
  // importing and scoring) can take a couple of minutes, so it runs in the
  // background rather than holding the dialog open.
  const syncThen = () => {
    invalidate()
    onDone()
    toast.info('Naukri connected', 'Pulling your recommended jobs. This can take a minute or two.')
    integrationApi
      .sync(30)
      .then((result) => {
        toast.success(
          'Your Naukri jobs are ready',
          `${result.jobsImported} job${result.jobsImported === 1 ? '' : 's'} added to your feed.`,
        )
      })
      .catch((error: ApiError) => {
        // Another sync already running is fine: it fills the same feed.
        if (error.code !== 'sync_in_progress') {
          toast.error('Could not pull Naukri jobs', 'Try "Sync now" in Settings in a minute.')
        }
      })
      .finally(() => {
        invalidate()
        queryClient.invalidateQueries({ queryKey: ['job-sources'] })
        queryClient.invalidateQueries({ queryKey: ['jobs'] })
      })
  }

  const connectPassword = useMutation({
    mutationFn: () => integrationApi.connectNaukri({ identifier, password }),
    onSuccess: syncThen,
    onError: (error: ApiError) => toast.error('Could not connect', error.message),
  })

  const startOtp = useMutation({
    mutationFn: (number: string) => integrationApi.startOtp(number),
    onSuccess: (challenge) => {
      setChallengeId(challenge.challengeId)
      goTo('otp')
      toast.info('Code sent', `We texted a code to ${challenge.mobileNumber}.`)
    },
    onError: (error: ApiError) => toast.error('Could not send the code', error.message),
  })

  // The account is made in the background (JobSlide address + portal password); it connects
  // itself when it is done. Naukri may text a code: the live page asks for it then.
  const [handoffPath, setHandoffPath] = useState<string | null>(null)
  const createAccount = useMutation({
    mutationFn: () =>
      integrationApi.createAccount({
        ...(mobile.trim() ? { mobileNumber: mobile.trim() } : {}),
        ...(fullName.trim() ? { fullName: fullName.trim() } : {}),
      }),
    onSuccess: (started) => {
      setHandoffPath(started.handoffPath)
      goTo('creating')
      queryClient.invalidateQueries({ queryKey: ['job-portals'] })
    },
    onError: (error: ApiError) => toast.error('Could not create the account', error.message),
  })

  const verifyOtp = useMutation({
    mutationFn: () => integrationApi.verifyOtp({ challengeId: challengeId!, code }),
    onSuccess: syncThen,
    onError: (error: ApiError) => toast.error('That code did not work', error.message),
  })

  if (screen === 'intro') {
    return (
      <div className="space-y-8">
        <ul className="space-y-3">
          {[
            'See Naukri matches next to your other recommended jobs',
            'Create a new account or use one you already have',
            'Skip anytime. Nothing blocks the rest of setup',
          ].map((line) => (
            <li key={line} className="flex items-start gap-3 text-[16px] leading-relaxed text-ink-soft">
              <CheckBadge />
              {line}
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-4 border-t border-line pt-7">
          <Button size="lg" onClick={() => goTo('choose')}>
            Connect Naukri
            <EnterChip />
          </Button>
          <SkipLink onClick={onSkip} />
        </div>
      </div>
    )
  }

  if (screen === 'choose') {
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          <ChoiceCard
            icon={<PersonIcon />}
            title="I have a Naukri account"
            description="Use your password or registered mobile number"
            onClick={() => goTo('signin')}
          />
          <ChoiceCard
            icon={<BoltIcon />}
            title="Create one for me"
            description="We make it with your JobSlide email and portal password"
            onClick={() => goTo('create')}
          />
        </div>

        <ol className="divide-y divide-line border-y border-line">
          {[
            'We pull the recommendations Naukri shows you, plus roles matching your profile',
            'They appear in your feed with match scores, like any other job',
            'Apply in one click. We answer screening questions for you',
          ].map((line, index) => (
            <li key={line} className="flex items-start gap-3 py-3.5 text-[15px] text-ink-soft">
              <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-brand-soft text-[12px] font-medium tabular-nums text-brand">
                {index + 1}
              </span>
              {line}
            </li>
          ))}
        </ol>

        <p className="flex items-center gap-2 text-[14px] text-ink-muted">
          <LockIcon /> Encrypted, used only to sign you in, removable anytime.
        </p>
        <SkipLink onClick={onSkip} />
      </div>
    )
  }

  if (screen === 'signin') {
    return (
      <div className="space-y-6">
        <BackLink onClick={() => goTo('choose')} />

        <div>
          <p className="mb-3 text-[15px] text-ink-soft">How do you want to sign in?</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <MethodCard
              selected={method === 'password'}
              onClick={() => setMethod('password')}
              title="Password"
              description="Email, username, or mobile"
            />
            <MethodCard
              selected={method === 'otp'}
              onClick={() => setMethod('otp')}
              title="Mobile OTP"
              description="No password needed"
            />
          </div>
        </div>

        {method === 'password' ? (
          <>
            <TextField
              label="Email, username, or mobile number"
              appearance="box"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              placeholder="Email, username, or mobile"
              autoComplete="username"
            />
            <TextField
              label="Password"
              appearance="box"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Your Naukri password"
              autoComplete="current-password"
            />
            <Button
              fullWidth
              size="lg"
              onClick={() => connectPassword.mutate()}
              disabled={!identifier.trim() || !password}
              loading={connectPassword.isPending}
            >
              Connect
            </Button>
          </>
        ) : (
          <>
            <TextField
              label="Registered mobile number"
              appearance="box"
              value={mobile}
              onChange={(event) => setMobile(event.target.value)}
              placeholder="+91 91334 53036"
              inputMode="tel"
              autoComplete="tel"
            />
            <Button
              fullWidth
              size="lg"
              onClick={() => startOtp.mutate(mobile)}
              disabled={mobile.trim().length < 6}
              loading={startOtp.isPending}
            >
              Send code
            </Button>
          </>
        )}

        <SkipLink onClick={onSkip} />
      </div>
    )
  }

  if (screen === 'create') {
    return (
      <div className="space-y-6">
        <BackLink onClick={() => goTo('choose')} />
        <TextField
          label="Full name"
          appearance="box"
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
          placeholder="Your name as it should appear"
          autoComplete="name"
        />
        <TextField
          label="Mobile number"
          appearance="box"
          value={mobile}
          onChange={(event) => setMobile(event.target.value)}
          placeholder="+91 91334 53036"
          hint="Naukri asks for one. Leave empty to use the number on your profile. If Naukri texts a code and won't let us skip it, we ask you for it."
          inputMode="tel"
          autoComplete="tel"
        />
        <Button
          fullWidth
          size="lg"
          onClick={() => createAccount.mutate()}
          disabled={mobile.trim().length > 0 && mobile.trim().length < 6}
          loading={createAccount.isPending}
        >
          Create my account
        </Button>
        <SkipLink onClick={onSkip} />
      </div>
    )
  }

  if (screen === 'creating') {
    return (
      <div className="space-y-6">
        <div className="rounded-card border border-line bg-surface-muted px-4 py-4">
          <p className="text-[16px] font-medium text-ink">We are creating your Naukri account</p>
          <p className="mt-1.5 text-[14px] leading-relaxed text-ink-soft">
            It runs in the background with your JobSlide email and portal password, and fills your Naukri profile
            from this one. When it is done, Naukri shows as connected and its jobs appear in your feed. If Naukri
            texts a code to your phone, we email you and ask for it on the live page.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="lg" onClick={onDone}>
            Done
          </Button>
          {handoffPath && (
            <a
              href={handoffPath}
              className="text-[15px] text-ink-muted underline underline-offset-4 transition-colors hover:text-ink"
            >
              Open the live page
            </a>
          )}
        </div>
      </div>
    )
  }

  // screen === 'otp'
  return (
    <div className="space-y-6">
      <BackLink onClick={() => goTo('choose')} />
      <TextField
        label="Verification code"
        appearance="box"
        value={code}
        onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="6-digit code"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        className="text-center font-mono text-2xl tracking-[0.4em]"
      />
      <Button
        fullWidth
        size="lg"
        onClick={() => verifyOtp.mutate()}
        disabled={code.length < 4}
        loading={verifyOtp.isPending}
      >
        Verify &amp; connect
      </Button>
      <button
        type="button"
        onClick={() => startOtp.mutate(mobile)}
        disabled={startOtp.isPending}
        className="text-[14px] text-ink-muted underline underline-offset-4 transition-colors hover:text-ink disabled:opacity-50"
      >
        Send a new code
      </button>
      <SkipLink onClick={onSkip} />
    </div>
  )
}

// ---------------------------------------------------------------------------
function ChoiceCard({
  icon,
  title,
  description,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-3.5 rounded-card border border-line bg-surface px-4 py-4 text-left transition-[border-color,box-shadow,background-color] hover:border-brand/40 hover:bg-brand-soft/40 hover:shadow-card"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-brand-soft text-brand">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-medium text-ink">{title}</span>
        <span className="mt-0.5 block text-[13px] text-ink-muted">{description}</span>
      </span>
      <svg className="h-4 w-4 shrink-0 text-ink-faint transition-[transform,color] group-hover:translate-x-0.5 group-hover:text-ink" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="m6 3 5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </button>
  )
}

function MethodCard({
  selected,
  onClick,
  title,
  description,
}: {
  selected: boolean
  onClick: () => void
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={`flex items-start gap-3 rounded-card border px-4 py-3 text-left transition-colors ${
        selected ? 'border-accent-line bg-accent-soft/60' : 'border-line hover:bg-surface-muted'
      }`}
    >
      <span
        className={`mt-1 grid h-[17px] w-[17px] shrink-0 place-items-center rounded-full border-2 ${
          selected ? 'border-ink' : 'border-line-strong'
        }`}
      >
        {selected && <span className="h-[7px] w-[7px] rounded-full bg-ink" />}
      </span>
      <span>
        <span className="block text-[15px] font-medium text-ink">{title}</span>
        <span className="mt-0.5 block text-[13px] text-ink-muted">{description}</span>
      </span>
    </button>
  )
}

function SkipLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[15px] text-ink-muted underline underline-offset-4 transition-colors hover:text-ink"
    >
      Skip for now
    </button>
  )
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 text-[15px] text-ink-soft transition-colors hover:text-ink"
    >
      ← Back
    </button>
  )
}

function CheckBadge() {
  return (
    <span className="mt-[3px] grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
      <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="m2.5 6.2 2.2 2.2L9.5 3.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

function PersonIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="6.5" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 16.5c0-2.8 2.7-4.5 6-4.5s6 1.7 6 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function BoltIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M11 2 4.5 11h4l-.5 7 7-9.5h-4L11 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}
